import { formatNumber } from "@telerik/kendo-intl";
import React, { Component, PureComponent } from "react";
import ReactApexChart from "react-apexcharts";
import "../../Styles/HistogramChart.scss";
import { Switch } from '@progress/kendo-react-inputs';
import HistogramLineChart from "./HistogramLineChart";
import Images from "../../Assets/Images";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import EmailService from "../../Services/EmailService";
import EmailChartPopup from "../Common/EmailChartPopup";

export default class HistogramChart extends Component {
  constructor(props) {
    super(props);
    this.state = {checked:false};
    // Create refs for both chart types
    this.chartRef = React.createRef();
    this.lineChartRef = React.createRef();
  }

  // Helper function to download chart as PDF using direct ApexCharts export with improved quality
  downloadChartAsPDF = (fileName) => {
    try {
      console.log("Starting PDF export...");
      
      // Get chart title from props or use default based on chart type
      const defaultTitle = this.state.checked ? "Probability Histogram Chart" : "P-Value Histogram Chart";
      let chartTitle = this.props.title || defaultTitle;
      
      // If title doesn't already have a format like "X - Y", add the format
      if (this.props.title && !this.props.title.includes(" - ") && this.props.title != "Realised Opportunity") {
        const threatOrOpportunity = this.props.isOpportunity ? "Opportunity" : "Threat";
        const titlePrefix = this.props.isOpportunity ? "Unrealised" : "Pre-Mitigated";
        chartTitle = `${titlePrefix} - ${threatOrOpportunity}`;
      }
      const currentDate = new Date().toLocaleDateString();
      
      // Create PDF with better quality settings
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true,
        precision: 4
      });
      
      // Set PDF metadata
      pdf.setProperties({
        title: chartTitle,
        subject: chartTitle,
        author: 'Riskwell',
        keywords: 'chart, export',
        creator: 'Riskwell Application'
      });
      
      // Get page dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Function to add chart image to PDF with improved quality
      const addChartToPDF = (imgURI) => {
        // Create an image element to get dimensions
        const img = new Image();
        img.src = imgURI;
        
        // Once the image is loaded, add it to the PDF
        img.onload = () => {
          // Set PDF background color to match the application's dark theme
          // pdf.setFillColor(31, 34, 34); // #1f2222 in RGB
          // pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
          
          // Add title to PDF - after setting background
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor('#000000'); 
          pdf.text(chartTitle, pdfWidth / 2, 20, { align: 'center' });
          
          // Add date
          pdf.setFontSize(10);
          pdf.text(`Generated: ${currentDate}`, pdfWidth - 20, 10, { align: 'right' });
          
          // Calculate dimensions for better quality
          const imgWidth = img.width;
          const imgHeight = img.height;
          
          // Use a larger portion of the page for the chart
          const maxWidth = pdfWidth * 0.9; // Use 90% of page width
          const maxHeight = pdfHeight * 0.6; // Use 60% of page height
          
          const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
          const scaledWidth = imgWidth * ratio;
          const scaledHeight = imgHeight * ratio;
          
          // Center the image
          const imgX = (pdfWidth - scaledWidth) / 2;
          const imgY = 30; // Position below the title
          
          // Add border around the chart
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.5);
          pdf.rect(imgX - 2, imgY - 2, scaledWidth + 4, scaledHeight + 4);
          
          // Add the chart image with high quality
          pdf.addImage(imgURI, 'PNG', imgX, imgY, scaledWidth, scaledHeight, null, 'FAST');
          
          // Add data table from the chart
          this.addDataTableToPDF(pdf, imgY + scaledHeight + 10);
          
          // Add footer
          pdf.setFontSize(8);
          pdf.setTextColor(200, 200, 200); // Light gray for footer
          pdf.text(chartTitle, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
          
          // Save the PDF
          pdf.save(fileName);
          console.log("PDF saved successfully");
        };
      };
      
      // Create a temporary copy of the chart options with title for export
      const exportOptions = this.state.checked ?
        JSON.parse(JSON.stringify(this.state.probabilityOptions)) :
        JSON.parse(JSON.stringify(this.lineChartRef.current.state.pValueOptions));
      
      if (!this.state.checked) {
        console.log("PDF export: Preserving original colors for line chart");
        const originalColors = exportOptions.colors ? [...exportOptions.colors] : [];
        
        if (exportOptions.xaxis) {
          if (exportOptions.xaxis.title) {
            exportOptions.xaxis.title.style = { ...exportOptions.xaxis.title.style, color: "#000000" };
          }
          if (exportOptions.xaxis.labels && exportOptions.xaxis.labels.style) {
            exportOptions.xaxis.labels.style = { ...exportOptions.xaxis.labels.style, colors: Array(50).fill("#000000") };
          }
          // Ensure axis lines are black
          if (exportOptions.xaxis.axisBorder) {
            exportOptions.xaxis.axisBorder.color = "#000000";
          } else {
            exportOptions.xaxis.axisBorder = { color: "#000000", show: true };
          }
          // Ensure axis ticks are black
          if (exportOptions.xaxis.axisTicks) {
            exportOptions.xaxis.axisTicks.color = "#000000";
          } else {
            exportOptions.xaxis.axisTicks = { color: "#000000", show: true };
          }
        }
        
        if (exportOptions.yaxis) {
          if (Array.isArray(exportOptions.yaxis)) {
            exportOptions.yaxis.forEach(axis => {
              if (axis.title) {
                axis.title.style = { ...axis.title.style, color: "#000000" };
              }
              if (axis.labels && axis.labels.style) {
                axis.labels.style = { ...axis.labels.style, colors: Array(50).fill("#000000") };
              }
              if (axis.axisBorder) {
                axis.axisBorder.color = "#000000";
              } else {
                axis.axisBorder = { color: "#000000", show: true };
              }
              if (axis.axisTicks) {
                axis.axisTicks.color = "#000000";
              } else {
                axis.axisTicks = { color: "#000000", show: true };
              }
            });
          } else {
            if (exportOptions.yaxis.title) {
              exportOptions.yaxis.title.style = { ...exportOptions.yaxis.title.style, color: "#000000" };
            }
            if (exportOptions.yaxis.labels && exportOptions.yaxis.labels.style) {
              exportOptions.yaxis.labels.style = { ...exportOptions.yaxis.labels.style, colors: Array(50).fill("#000000") };
            }
            if (exportOptions.yaxis.axisBorder) {
              exportOptions.yaxis.axisBorder.color = "#000000";
            } else {
              exportOptions.yaxis.axisBorder = { color: "#000000", show: true };
            }
            if (exportOptions.yaxis.axisTicks) {
              exportOptions.yaxis.axisTicks.color = "#000000";
            } else {
              exportOptions.yaxis.axisTicks = { color: "#000000", show: true };
            }
          }
        }
        
        // Set chart foreColor to black to ensure all text is black
        if (exportOptions.chart) {
          exportOptions.chart.foreColor = "#000000";
        } else {
          exportOptions.chart = { foreColor: "#000000" };
        }
        
        // Restore the original colors for the line series
        exportOptions.colors = originalColors;
        console.log("PDF export: Original colors restored for line chart", originalColors);
      } else {
        // For bar chart, set all colors to black
        if (exportOptions.xaxis) {
          if (exportOptions.xaxis.title) {
            exportOptions.xaxis.title.style = { ...exportOptions.xaxis.title.style, color: "#000000" };
          }
          if (exportOptions.xaxis.labels && exportOptions.xaxis.labels.style) {
            exportOptions.xaxis.labels.style = { ...exportOptions.xaxis.labels.style, colors: "#000000" };
          }
        }
        
        if (exportOptions.yaxis) {
          if (Array.isArray(exportOptions.yaxis)) {
            exportOptions.yaxis.forEach(axis => {
              if (axis.title) {
                axis.title.style = { ...axis.title.style, color: "#000000" };
              }
              if (axis.labels && axis.labels.style) {
                axis.labels.style = { ...axis.labels.style, colors: "#000000" };
              }
              if (axis.axisBorder) {
                axis.axisBorder.color = "#000000";
              }
            });
          } else {
            if (exportOptions.yaxis.title) {
              exportOptions.yaxis.title.style = { ...exportOptions.yaxis.title.style, color: "#000000" };
            }
            if (exportOptions.yaxis.labels && exportOptions.yaxis.labels.style) {
              exportOptions.yaxis.labels.style = { ...exportOptions.yaxis.labels.style, colors: "#000000" };
            }
            if (exportOptions.yaxis.axisBorder) {
              exportOptions.yaxis.axisBorder.color = "#000000";
            }
          }
        }
      }
      
      // Add title to the export options
      exportOptions.title = {
        text: chartTitle,
        align: "center",
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#1f2222'
        }
      };
      
      // Make sure title is at top level, not inside chart object
      if (exportOptions.chart && exportOptions.chart.title) {
        delete exportOptions.chart.title;
      }
      
      // Ensure chart foreColor is set to black for all text elements
      if (!exportOptions.chart) {
        exportOptions.chart = {};
      }
      exportOptions.chart.foreColor = "#000000";
      
      console.log("PDF export: Final export options prepared with black axes and text");
      
      // Get chart image with higher quality based on which chart is active
      const chartOptions = {
        background: '#ffffff',
        scale: 4,
        quality: 1,
        format: 'png',
        config: exportOptions
      };
      
      // Use the same SVG manipulation approach as PNG export
      if (this.state.checked) {
        // For bar chart
        if (this.chartRef.current && this.chartRef.current.chart) {
          const chart = this.chartRef.current.chart;
          
          // Get the chart's SVG element
          const chartEl = chart.el;
          if (!chartEl) {
            console.error("Chart element not found");
            return;
          }
          
          // Find the SVG element
          const svgEl = chartEl.querySelector('svg');
          if (!svgEl) {
            console.error("SVG element not found");
            return;
          }
          
          // Use our helper function to modify the SVG
          const modifiedSvg = this.modifySvgForExport(svgEl);
          
          // Convert the SVG to a data URL
          const svgData = new XMLSerializer().serializeToString(modifiedSvg);
          const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
          const svgUrl = URL.createObjectURL(svgBlob);
          
          // Create an image from the SVG
          const img = new Image();
          img.src = svgUrl;
          
          img.onload = () => {
            // Create a canvas with the SVG
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            
            // Set white background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Draw the chart image
            ctx.drawImage(img, 0, 0);
            
            // Convert to PNG and use for PDF
            const finalImgURI = canvas.toDataURL('image/png');
            addChartToPDF(finalImgURI);
            
            // Clean up
            URL.revokeObjectURL(svgUrl);
          };
          
          img.onerror = (error) => {
            console.error("Error loading SVG image for PDF:", error);
            URL.revokeObjectURL(svgUrl);
            
            // Fallback to original method if SVG approach fails
            chart.dataURI(chartOptions)
              .then(({ imgURI }) => {
                addChartToPDF(imgURI);
              })
              .catch(fallbackError => {
                console.error('Fallback PDF export error:', fallbackError);
              });
          };
        }
      } else {
        // For histogram line chart
        if (this.lineChartRef.current &&
            this.lineChartRef.current.chartRef &&
            this.lineChartRef.current.chartRef.current &&
            this.lineChartRef.current.chartRef.current.chart) {
          
          const chart = this.lineChartRef.current.chartRef.current.chart;
          
          // Get the chart's SVG element
          const chartEl = chart.el;
          if (!chartEl) {
            console.error("Chart element not found");
            return;
          }
          
          // Find the SVG element
          const svgEl = chartEl.querySelector('svg');
          if (!svgEl) {
            console.error("SVG element not found");
            return;
          }
          
          // Use our helper function to modify the SVG
          const modifiedSvg = this.modifySvgForExport(svgEl);
          
          // Convert the SVG to a data URL
          const svgData = new XMLSerializer().serializeToString(modifiedSvg);
          const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
          const svgUrl = URL.createObjectURL(svgBlob);
          
          // Create an image from the SVG
          const img = new Image();
          img.src = svgUrl;
          
          img.onload = () => {
            // Create a canvas with the SVG
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            
            // Set white background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Draw the chart image
            ctx.drawImage(img, 0, 0);
            
            // Convert to PNG and use for PDF
            const finalImgURI = canvas.toDataURL('image/png');
            addChartToPDF(finalImgURI);
            
            // Clean up
            URL.revokeObjectURL(svgUrl);
          };
          
          img.onerror = (error) => {
            console.error("Error loading SVG image for PDF:", error);
            URL.revokeObjectURL(svgUrl);
            
            // Fallback to original method if SVG approach fails
            chart.dataURI(chartOptions)
              .then(({ imgURI }) => {
                addChartToPDF(imgURI);
              })
              .catch(fallbackError => {
                console.error('Fallback PDF export error:', fallbackError);
              });
          };
        }
      }
    } catch (error) {
      console.error('Error creating PDF:', error);
    }
  };
  
  // Helper function to add data table to PDF
  addDataTableToPDF = (pdf, yPosition) => {
    try {
      const histogramResponse = this.props.histogramResponse || {
        sCurveValue: [],
        sCurevePercentage: []
      };
      
      if (histogramResponse.sCurveValue && histogramResponse.sCurveValue.length > 0) {
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        // Add table title - only on second page, not on first page
        pdf.setFontSize(12);
        // Don't add title on first page, it will be added only when table moves to second page
        
        // Set up table
        const tableWidth = Math.min(pdfWidth * 0.8, 150);
        const startX = (pdfWidth - tableWidth) / 2;
        let startY = yPosition + 5;
        const rowHeight = 7; // Slightly smaller rows to fit more
        const colWidth = tableWidth / 2;
        
        // Calculate if we need a new page for the table
        const totalRows = histogramResponse.sCurveValue.length;
        const estimatedTableHeight = (totalRows + 1) * rowHeight; // +1 for header
        const remainingSpace = pdfHeight - startY - 15; // 15px margin at bottom
        
        // If table won't fit on current page, add a new page
        if (estimatedTableHeight > remainingSpace) {
          pdf.addPage();
          startY = 20; // Start at top of new page
          
          // Add title on new page - ensure text color is black
          pdf.setTextColor(0, 0, 0); // Black text for page 2 title
          const impactName = histogramResponse.impactName || "";
          
          // Split the text to make impactName bold
          const normalText = "Probability Values - ";
          
          // Calculate positions for centered text
          const textWidth = pdf.getStringUnitWidth(normalText + impactName) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
          const startX = (pdfWidth - textWidth) / 2;
          
          // Add normal text first
          pdf.setFont(undefined, 'normal');
          pdf.text(normalText, startX, startY);
          
          // Add impactName in bold
          const normalTextWidth = pdf.getStringUnitWidth(normalText) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
          pdf.setFont(undefined, 'bold');
          pdf.text(impactName, startX + normalTextWidth, startY);
          
          // Reset font back to normal for the rest of the document
          pdf.setFont(undefined, 'normal');
          startY += 10;
        }
        
        // Draw table header
        pdf.setFillColor(240, 240, 240);
        pdf.rect(startX, startY, tableWidth, rowHeight, 'F');
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(startX, startY, tableWidth, rowHeight);
        pdf.line(startX + colWidth, startY, startX + colWidth, startY + rowHeight);
        
        // Add header text
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Percentile', startX + colWidth / 2, startY + rowHeight / 2 + 1, { align: 'center' });
        pdf.text('Value', startX + colWidth + colWidth / 2, startY + rowHeight / 2 + 1, { align: 'center' });
        
        // Draw table rows
        startY += rowHeight;
        
        // Show all rows - no limit
        for (let i = 0; i < totalRows; i++) {
          // Check if we need to add a new page
          if (startY + rowHeight > pdfHeight - 15) {
            pdf.addPage();
            startY = 20;
            
            // Redraw header on new page
            pdf.setFillColor(240, 240, 240);
            pdf.rect(startX, startY, tableWidth, rowHeight, 'F');
            pdf.setDrawColor(200, 200, 200);
            pdf.rect(startX, startY, tableWidth, rowHeight);
            pdf.line(startX + colWidth, startY, startX + colWidth, startY + rowHeight);
            
            pdf.setFontSize(10);
            pdf.text('Percentile', startX + colWidth / 2, startY + rowHeight / 2 + 1, { align: 'center' });
            pdf.text('Value', startX + colWidth + colWidth / 2, startY + rowHeight / 2 + 1, { align: 'center' });
            
            startY += rowHeight;
          }
          
          // Draw row
          if (i % 2 === 0) {
            pdf.setFillColor(250, 250, 250);
            pdf.rect(startX, startY, tableWidth, rowHeight, 'F');
          }
          
          pdf.rect(startX, startY, tableWidth, rowHeight);
          pdf.line(startX + colWidth, startY, startX + colWidth, startY + rowHeight);
          
          // Add row data
          pdf.setFontSize(9);
          const percentile = histogramResponse.sCurevePercentage[i] || '';
          const value = histogramResponse.sCurveValue[i] || '';
          
          pdf.text(`P(${percentile})`, startX + colWidth / 2, startY + rowHeight / 2 + 1, { align: 'center' });
          pdf.text(formatNumber(value, "##,#"), startX + colWidth + colWidth / 2, startY + rowHeight / 2 + 1, { align: 'center' });
          
          startY += rowHeight;
        }
      }
    } catch (error) {
      console.error('Error adding data table to PDF:', error);
    }
  };

  // Helper function to modify SVG for export - keeps line chart line green while making axes black
  modifySvgForExport = (svgEl) => {
    if (!svgEl) return null;
    
    // Clone the SVG to avoid modifying the original
    const svgClone = svgEl.cloneNode(true);
    
    // Set the background to white
    const rectElements = svgClone.querySelectorAll('rect');
    rectElements.forEach(rect => {
      // Find the background rect (usually the first one or one with specific class)
      if (rect.getAttribute('width') === svgClone.getAttribute('width') ||
          rect.getAttribute('class')?.includes('background') ||
          rect.getAttribute('height') === svgClone.getAttribute('height')) {
        rect.setAttribute('fill', '#ffffff');
      }
    });
    
    // Find all text elements in the SVG
    const textElements = svgClone.querySelectorAll('text');
    
    // Change all text colors to black
    textElements.forEach(text => {
      text.setAttribute('fill', '#000000');
    });
    
    // Handle line elements - change axis lines to black and grid lines to light grey
    const lineElements = svgClone.querySelectorAll('line');
    lineElements.forEach((line) => {
      // Check if this is a grid line or dotted line
      const strokeDasharray = line.getAttribute('stroke-dasharray');
      const className = line.getAttribute('class') || '';
      
      if (strokeDasharray && strokeDasharray !== '0') {
        // This is a dotted/dashed line - remove it completely
        line.setAttribute('stroke-opacity', '0');
        line.setAttribute('visibility', 'hidden');
      } else if (className.includes('grid') || className.includes('gridline')) {
        // This is a grid line - make it light grey
        line.setAttribute('stroke', '#e0e0e0');
        line.setAttribute('stroke-opacity', '0.5');
      } else if (line.getAttribute('stroke')) {
        // This is a regular line (like axis) - change to black
        line.setAttribute('stroke', '#000000');
      }
    });
    
    // Find grid elements and make them light grey
    const gridElements = svgClone.querySelectorAll('.apexcharts-grid, .apexcharts-grid-borders, .apexcharts-gridlines');
    gridElements.forEach(grid => {
      // Make grid lines light grey
      const gridLines = grid.querySelectorAll('line');
      gridLines.forEach(line => {
        line.setAttribute('stroke', '#e0e0e0');
        line.setAttribute('stroke-opacity', '0.5');
        line.setAttribute('stroke-dasharray', '0'); // Make it solid
      });
      
      // Make sure the grid is visible
      grid.setAttribute('visibility', 'visible');
    });
    
    // Remove any yellow highlights or unwanted elements
    const highlightElements = svgClone.querySelectorAll('[fill="yellow"], [stroke="yellow"]');
    highlightElements.forEach(el => {
      if (el.tagName.toLowerCase() === 'rect') {
        el.setAttribute('fill-opacity', '0');
        el.setAttribute('stroke-opacity', '0');
      }
    });
    
    // Specifically target and remove any vertical lines that might be causing the dotted line issue
    const verticalLines = svgClone.querySelectorAll('line[y1][y2]');
    verticalLines.forEach(line => {
      const x1 = parseFloat(line.getAttribute('x1') || '0');
      const x2 = parseFloat(line.getAttribute('x2') || '0');
      
      // If this is a vertical line (x1 === x2)
      if (Math.abs(x1 - x2) < 1) {
        // Check if it's in the problematic range
        const svgWidth = svgClone.getAttribute('width') || '800';
        const relativePosition = x1 / parseFloat(svgWidth);
        
        // If it's in the right 20% of the chart
        if (relativePosition > 0.8) {
          line.setAttribute('stroke-opacity', '0');
          line.setAttribute('visibility', 'hidden');
        }
      }
    });
    
    // Also remove any path elements that might be causing dotted lines
    const dashedPaths = svgClone.querySelectorAll('path[stroke-dasharray]');
    dashedPaths.forEach(path => {
      path.setAttribute('stroke-dasharray', '0');
      
      // If it has a class that suggests it's a special marker or annotation
      const className = path.getAttribute('class') || '';
      if (className.includes('marker') || className.includes('annotation')) {
        path.setAttribute('stroke-opacity', '0');
        path.setAttribute('visibility', 'hidden');
      }
    });
    
    // For line chart, modify path elements to keep the line green while making axes black
    const pathElements = svgClone.querySelectorAll('path');
    pathElements.forEach(path => {
      // Check if this is a line chart path or an axis path
      const className = path.getAttribute('class') || '';
      
      // If it's a line chart path, keep its original color (green)
      if (className.includes('apexcharts-line') || className.includes('apexcharts-series')) {
        // Keep the original color (green for line chart)
      }
      // Otherwise, if it's an axis path, change to black
      else if (path.getAttribute('stroke')) {
        path.setAttribute('stroke', '#000000');
      }
    });
    
    // For bar charts, remove all borders
    if (this.state.checked) {
      // 1. Target all rectangles in the SVG
      const allRects = svgClone.querySelectorAll('rect');
      allRects.forEach(rect => {
        // Remove all stroke-related attributes
        rect.setAttribute('stroke', 'none');
        rect.setAttribute('stroke-width', '0');
        rect.setAttribute('stroke-opacity', '0');
        rect.setAttribute('stroke-dasharray', '0');
        
        // Also set inline style to override any CSS
        const currentStyle = rect.getAttribute('style') || '';
        rect.setAttribute('style', `${currentStyle}; stroke: none !important; stroke-width: 0 !important;`);
      });
      
      // 2. Target specific bar series elements
      const barSeries = svgClone.querySelectorAll('.apexcharts-bar-series, .apexcharts-plot-series, .apexcharts-series');
      barSeries.forEach(series => {
        // Set series-level stroke attributes
        series.setAttribute('stroke', 'none');
        series.setAttribute('stroke-width', '0');
        
        // Find all child elements and remove strokes
        const children = series.querySelectorAll('*');
        children.forEach(child => {
          child.setAttribute('stroke', 'none');
          child.setAttribute('stroke-width', '0');
          child.setAttribute('stroke-opacity', '0');
        });
      });
      
      // 3. Target any element with a stroke attribute
      const strokedElements = svgClone.querySelectorAll('[stroke]');
      strokedElements.forEach(el => {
        // Skip axis and grid elements
        const className = el.getAttribute('class') || '';
        if (!className.includes('axis') && !className.includes('grid') &&
            !className.includes('tick') && el.tagName.toLowerCase() !== 'line') {
          el.setAttribute('stroke', 'none');
          el.setAttribute('stroke-width', '0');
          el.setAttribute('stroke-opacity', '0');
        }
      });
      
      // 4. Add inline CSS to force no borders
      const styleElement = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      styleElement.textContent = `
        .apexcharts-bar-series rect,
        .apexcharts-series rect,
        .apexcharts-plot-series rect,
        .apexcharts-bar-area,
        .apexcharts-bar {
          stroke: none !important;
          stroke-width: 0 !important;
          stroke-opacity: 0 !important;
        }
      `;
      svgClone.appendChild(styleElement);
      
      // 5. Try to modify the defs section if it exists (sometimes contains stroke definitions)
      const defs = svgClone.querySelector('defs');
      if (defs) {
        const patterns = defs.querySelectorAll('pattern');
        patterns.forEach(pattern => {
          const patternRects = pattern.querySelectorAll('rect');
          patternRects.forEach(rect => {
            rect.setAttribute('stroke', 'none');
            rect.setAttribute('stroke-width', '0');
          });
        });
      }
    }
    
    return svgClone;
  }
  
  // Method to export chart as PNG
  onImageExportClick = () => {
    // Get chart title from props or use default
    const defaultTitle = this.state.checked ? "Probability Histogram Chart" : "P-Value Histogram Chart";
    let chartTitle = this.props.title || defaultTitle;
    
    // If title doesn't already have a format like "X - Y", add the format
    if (this.props.title && !this.props.title.includes(" - ") && this.props.title != "Realised Opportunity") {
      const threatOrOpportunity = this.props.isOpportunity ? "Opportunity" : "Threat";
      const titlePrefix = this.props.isOpportunity ? "Unrealised" : "Pre-Mitigated";
      chartTitle = `${titlePrefix} - ${threatOrOpportunity}`;
    }
    
    if (this.state.checked) {
      if (this.chartRef.current && this.chartRef.current.chart) {
        const chart = this.chartRef.current.chart;
        
        // Get the chart's SVG element
        const chartEl = chart.el;
        if (!chartEl) {
          console.error("Chart element not found");
          return;
        }
        
        // Find the SVG element
        const svgEl = chartEl.querySelector('svg');
        if (!svgEl) {
          console.error("SVG element not found");
          return;
        }
        
        // Use our helper function to modify the SVG
        const svgClone = this.modifySvgForExport(svgEl);
        
        // Find grid elements and make them light grey
        const gridElements = svgClone.querySelectorAll('.apexcharts-grid, .apexcharts-grid-borders, .apexcharts-gridlines');
        gridElements.forEach(grid => {
          // Make grid lines light grey
          const gridLines = grid.querySelectorAll('line');
          gridLines.forEach(line => {
            line.setAttribute('stroke', '#e0e0e0');
            line.setAttribute('stroke-opacity', '0.5');
            line.setAttribute('stroke-dasharray', '0'); // Make it solid
          });
          
          // Make sure the grid is visible
          grid.setAttribute('visibility', 'visible');
        });
        
        // Remove any yellow highlights or unwanted elements
        const highlightElements = svgClone.querySelectorAll('[fill="yellow"], [stroke="yellow"]');
        highlightElements.forEach(el => {
          if (el.tagName.toLowerCase() === 'rect') {
            el.setAttribute('fill-opacity', '0');
            el.setAttribute('stroke-opacity', '0');
          }
        });
        
        // Specifically target and remove any vertical lines that might be causing the dotted line issue
        const verticalLines = svgClone.querySelectorAll('line[y1][y2]');
        verticalLines.forEach(line => {
          const x1 = parseFloat(line.getAttribute('x1') || '0');
          const x2 = parseFloat(line.getAttribute('x2') || '0');
          
          // If this is a vertical line (x1 === x2)
          if (Math.abs(x1 - x2) < 1) {
            // Check if it's in the problematic range (42,390 - 47,100)
            // We can't check exact values, but we can check if it's in the right part of the SVG
            // by comparing to the SVG width
            const svgWidth = svgClone.getAttribute('width') || '800';
            const relativePosition = x1 / parseFloat(svgWidth);
            
            // If it's in the right 20% of the chart (where 42,390 - 47,100 would be)
            if (relativePosition > 0.8) {
              line.setAttribute('stroke-opacity', '0');
              line.setAttribute('visibility', 'hidden');
            }
          }
        });
        
        // Also remove any path elements that might be causing dotted lines
        const dashedPaths = svgClone.querySelectorAll('path[stroke-dasharray]');
        dashedPaths.forEach(path => {
          path.setAttribute('stroke-dasharray', '0');
          
          // If it has a class that suggests it's a special marker or annotation
          const className = path.getAttribute('class') || '';
          if (className.includes('marker') || className.includes('annotation')) {
            path.setAttribute('stroke-opacity', '0');
            path.setAttribute('visibility', 'hidden');
          }
        });
        // Convert the SVG to a data URL
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
        const svgUrl = URL.createObjectURL(svgBlob);
        
        // Create an image from the SVG
        const img = new Image();
        img.src = svgUrl;
        
        img.onload = () => {
          // Create a canvas with extra space for title
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height + 50;
          const ctx = canvas.getContext('2d');
          
          // Set white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Add title
          ctx.fillStyle = '#000000';
          ctx.font = 'bold 20px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(chartTitle, canvas.width / 2, 30);
          
          // Draw the chart image
          ctx.drawImage(img, 0, 50);
          
          // Convert to PNG and download
          const finalImgURI = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = finalImgURI;
          link.download = `${chartTitle}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up
          URL.revokeObjectURL(svgUrl);
        };
        
        img.onerror = (error) => {
          console.error("Error loading SVG image:", error);
          URL.revokeObjectURL(svgUrl);
        };
      }
    } else {
      // For histogram line chart - use a custom export function
      if (this.lineChartRef.current && this.lineChartRef.current.chartRef &&
          this.lineChartRef.current.chartRef.current &&
          this.lineChartRef.current.chartRef.current.chart) {
        
        const chart = this.lineChartRef.current.chartRef.current.chart;
        
        // Get the chart's SVG element
        const chartEl = chart.el;
        if (!chartEl) {
          console.error("Chart element not found");
          return;
        }
        
        // Find the SVG element
        const svgEl = chartEl.querySelector('svg');
        if (!svgEl) {
          console.error("SVG element not found");
          return;
        }
        
        // Use our helper function to modify the SVG
        const svgClone = this.modifySvgForExport(svgEl);
        
        // Convert the SVG to a data URL
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
        const svgUrl = URL.createObjectURL(svgBlob);
        
        // Create an image from the SVG
        const img = new Image();
        img.src = svgUrl;
        
        img.onload = () => {
          // Create a canvas with extra space for title
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height + 50;
          const ctx = canvas.getContext('2d');
          
          // Set white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Add title
          ctx.fillStyle = '#000000';
          ctx.font = 'bold 20px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(chartTitle, canvas.width / 2, 30);
          
          // Draw the chart image
          ctx.drawImage(img, 0, 50);
          
          // Convert to PNG and download
          const finalImgURI = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = finalImgURI;
          link.download = `${chartTitle}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up
          URL.revokeObjectURL(svgUrl);
        };
        
        img.onerror = (error) => {
          console.error("Error loading SVG image:", error);
          URL.revokeObjectURL(svgUrl);
        };
      }
    }
  };

  // Method to export chart as PDF
  onPDFExportClick = () => {
    console.log("PDF export clicked");
    // Get chart title from props or use default
    const defaultTitle = this.state.checked ? "Probability Histogram Chart" : "P-Value Histogram Chart";
    let chartTitle = this.props.title || defaultTitle;
    
    // If title doesn't already have a format like "X - Y", add the format
    if (this.props.title && !this.props.title.includes(" - ") && this.props.title != "Realised Opportunity") {
      const threatOrOpportunity = this.props.isOpportunity ? "Opportunity" : "Threat";
      const titlePrefix = this.props.isOpportunity ? "Unrealised" : "Pre-Mitigated";
      chartTitle = `${titlePrefix} - ${threatOrOpportunity}`;
    }
    this.downloadChartAsPDF(`${chartTitle}.pdf`);
  };
  
  // Method to export chart as email
  onEmailExportClick = async (emailId, message) => {
    try {
      console.log("Starting email export...");
      
      // Get chart title from props or use default
      const defaultTitle = this.state.checked ? "Probability Histogram Chart" : "P-Value Histogram Chart";
      let chartTitle = this.props.title || defaultTitle;
      
      // If title doesn't already have a format like "X - Y", add the format
      if (this.props.title && !this.props.title.includes(" - ") && this.props.title !== "Realised Opportunity") {
        const threatOrOpportunity = this.props.isOpportunity ? "Opportunity" : "Threat";
        const titlePrefix = this.props.isOpportunity ? "Unrealised" : "Pre-Mitigated";
        chartTitle = `${titlePrefix} - ${threatOrOpportunity}`;
      }
      
      // Use the same SVG manipulation approach as PNG and PDF exports
      if (this.state.checked) {
        // For bar chart
        if (this.chartRef.current && this.chartRef.current.chart) {
          const chart = this.chartRef.current.chart;
          
          // Get the chart's SVG element
          const chartEl = chart.el;
          if (!chartEl) {
            console.error("Chart element not found");
            return;
          }
          
          // Find the SVG element
          const svgEl = chartEl.querySelector('svg');
          if (!svgEl) {
            console.error("SVG element not found");
            return;
          }
          
          // Use our helper function to modify the SVG
          const modifiedSvg = this.modifySvgForExport(svgEl);
          
          // Convert the SVG to a data URL
          const svgData = new XMLSerializer().serializeToString(modifiedSvg);
          const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
          const svgUrl = URL.createObjectURL(svgBlob);
          
          // Create an image from the SVG
          const emailImg = new Image();
          emailImg.src = svgUrl;
          
          emailImg.onload = async () => {
            try {
              console.log("Email export: Image loaded successfully");
              
              // Create a canvas with extra space for the title - EXACTLY like PNG export
              const canvas = document.createElement('canvas');
              canvas.width = emailImg.width;
              canvas.height = emailImg.height + 50; // Add extra height for title
              const context = canvas.getContext('2d');
              
              // Set white background for the entire canvas
              context.fillStyle = '#ffffff';
              context.fillRect(0, 0, canvas.width, canvas.height);
              
              // Add title - EXACTLY like PNG export
              context.fillStyle = '#000000';
              context.font = 'bold 20px Arial';
              context.textAlign = 'center';
              context.fillText(chartTitle, canvas.width / 2, 30);
              
              // Draw the chart image below the title
              context.drawImage(emailImg, 0, 50);
              
              // Convert canvas to PNG
              const finalImgURI = canvas.toDataURL('image/png');
              console.log("Email export: Canvas converted to PNG successfully");
              
              // Send email using EmailService
              const result = await EmailService.sendChartEmail(
                finalImgURI,
                0, // widgetId
                emailId,
                message,
                `${chartTitle}.png`
              );
              
              if (result) {
                console.log('Email sent successfully with preserved line colors');
              }
              
              // Clean up
              URL.revokeObjectURL(svgUrl);
            } catch (error) {
              console.error('Error processing image for email:', error);
              URL.revokeObjectURL(svgUrl);
            }
          };
          
          emailImg.onerror = async (error) => {
            console.error("Error loading SVG image for email:", error);
            URL.revokeObjectURL(svgUrl);
            
            // Fallback to original method
            try {
              // Create a fallback using ApexCharts dataURI
              const chartOptions = {
                background: '#ffffff',
                foreColor: '#000000',
                config: {
                  title: {
                    text: chartTitle,
                    align: "center",
                    style: {
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#000000'
                    }
                  }
                }
              };
              
              const result = await chart.dataURI(chartOptions);
              const fallbackImg = new Image();
              fallbackImg.src = result.imgURI;
              
              fallbackImg.onload = async () => {
                // Create a canvas with extra space for title - EXACTLY like PNG export
                const canvas = document.createElement('canvas');
                canvas.width = fallbackImg.width;
                canvas.height = fallbackImg.height + 50;
                const context = canvas.getContext('2d');
                
                // Set white background for the entire canvas
                context.fillStyle = '#ffffff';
                context.fillRect(0, 0, canvas.width, canvas.height);
                
                // Add title - EXACTLY like PNG export
                context.fillStyle = '#000000';
                context.font = 'bold 20px Arial';
                context.textAlign = 'center';
                context.fillText(chartTitle, canvas.width / 2, 30);
                
                // Draw the chart image below the title
                context.drawImage(fallbackImg, 0, 50);
                
                // Convert canvas to PNG
                const finalImgURI = canvas.toDataURL('image/png');
                
                // Send email using EmailService
                const emailResult = await EmailService.sendChartEmail(
                  finalImgURI,
                  0, // widgetId
                  emailId,
                  message,
                  `${chartTitle}.png`
                );
                
                if (emailResult) {
                  console.log('Email sent successfully (fallback method)');
                }
              };
            } catch (fallbackError) {
              console.error('Fallback email export failed:', fallbackError);
            }
          };
        }
      } else {
        // For histogram line chart
        if (this.lineChartRef.current &&
            this.lineChartRef.current.chartRef &&
            this.lineChartRef.current.chartRef.current &&
            this.lineChartRef.current.chartRef.current.chart) {
          
          const chart = this.lineChartRef.current.chartRef.current.chart;
          
          // Get the chart's SVG element
          const chartEl = chart.el;
          if (!chartEl) {
            console.error("Chart element not found");
            return;
          }
          
          // Find the SVG element
          const svgEl = chartEl.querySelector('svg');
          if (!svgEl) {
            console.error("SVG element not found");
            return;
          }
          
          // Use our helper function to modify the SVG
          const modifiedSvg = this.modifySvgForExport(svgEl);
          
          // Convert the SVG to a data URL
          const svgData = new XMLSerializer().serializeToString(modifiedSvg);
          const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
          const svgUrl = URL.createObjectURL(svgBlob);
          
          // Create an image from the SVG
          const emailImg = new Image();
          emailImg.src = svgUrl;
          
          emailImg.onload = async () => {
            try {
              console.log("Email export: Image loaded successfully");
              
              // Create a canvas with extra space for the title - EXACTLY like PNG export
              const canvas = document.createElement('canvas');
              canvas.width = emailImg.width;
              canvas.height = emailImg.height + 50; // Add extra height for title
              const context = canvas.getContext('2d');
              
              // Set white background for the entire canvas
              context.fillStyle = '#ffffff';
              context.fillRect(0, 0, canvas.width, canvas.height);
              
              // Add title - EXACTLY like PNG export
              context.fillStyle = '#000000';
              context.font = 'bold 20px Arial';
              context.textAlign = 'center';
              context.fillText(chartTitle, canvas.width / 2, 30);
              
              // Draw the chart image below the title
              context.drawImage(emailImg, 0, 50);
              
              // Convert canvas to PNG
              const finalImgURI = canvas.toDataURL('image/png');
              console.log("Email export: Canvas converted to PNG successfully");
              
              // Send email using EmailService
              const result = await EmailService.sendChartEmail(
                finalImgURI,
                0, // widgetId
                emailId,
                message,
                `${chartTitle}.png`
              );
              
              if (result) {
                console.log('Email sent successfully with preserved line colors');
              }
              // Clean up
              URL.revokeObjectURL(svgUrl);
            } catch (error) {
              console.error('Error processing image for email:', error);
              URL.revokeObjectURL(svgUrl);
            }
          };
          
          // Handle image loading error
          emailImg.onerror = async (error) => {
            console.error('Error loading image for email:', error);
            URL.revokeObjectURL(svgUrl);
            
            // Fallback to original method
            try {
              // Create a fallback using ApexCharts dataURI
              const chartOptions = {
                background: '#ffffff',
                foreColor: '#000000',
                config: {
                  title: {
                    text: chartTitle,
                    align: "center",
                    style: {
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#000000'
                    }
                  }
                }
              };
              
              const result = await chart.dataURI(chartOptions);
              const fallbackImg = new Image();
              fallbackImg.src = result.imgURI;
              
              fallbackImg.onload = async () => {
                // Create a canvas with extra space for title - EXACTLY like PNG export
                const canvas = document.createElement('canvas');
                canvas.width = fallbackImg.width;
                canvas.height = fallbackImg.height + 50;
                const context = canvas.getContext('2d');
                
                // Set white background for the entire canvas
                context.fillStyle = '#ffffff';
                context.fillRect(0, 0, canvas.width, canvas.height);
                
                // Add title - EXACTLY like PNG export
                context.fillStyle = '#000000';
                context.font = 'bold 20px Arial';
                context.textAlign = 'center';
                context.fillText(chartTitle, canvas.width / 2, 30);
                
                // Draw the chart image below the title
                context.drawImage(fallbackImg, 0, 50);
                
                // Convert canvas to PNG
                const finalImgURI = canvas.toDataURL('image/png');
                
                // Send email using EmailService
                const emailResult = await EmailService.sendChartEmail(
                  finalImgURI,
                  0, // widgetId
                  emailId,
                  message,
                  `${chartTitle}.png`
                );
                
                if (emailResult) {
                  console.log('Email sent successfully (fallback method)');
                }
              };
            } catch (fallbackError) {
              console.error('Fallback email export failed:', fallbackError);
            }
          };
        }
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };
 

  createHistogramChart = async (response) => {
   
    if (!response.impact) {
      response["impact"] = [];
      response["frequency"] = [];
      response["sCurevePercentage"] = [];
      response["sCurveValue"] = [];
      response["sCurvePercentile"] = [];
      response["sCurvePercentileValue"] = [];
    }
     

    this.state = {
...this.state,
      probabilitySeries: [
        {
          name: "Probability",
          type: "column",
          data: response.frequency,
        },
      ],
      probabilityOptions: {
        chart: {
          height: 300,
          type: "column",
          // background: '#1f2222',
          // foreColor: '#f1f1f1',
          // theme: {
          //   mode: 'dark',
          //   palette: 'palette1'
          // }
        },
        // Title is only added during export, not in screen view
        plotOptions: {
          bar: {
            columnWidth: "98%",
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: [3, 3, 6],
          curve: "smooth",
          lineCap: "square",
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: [3, 3, 6],
          curve: "smooth",
          lineCap: "square",
        },
        // markers: {
        //   size: [1, 2]
        // },
        // Title is added only during export
        xaxis: {
          // min:0,
          categories: response.impact,
          type: "category",

          forceNiceScale: true,
          tickPlacement: "between",

          tickAmount: "dataPoints",
          // tickAmount:10,
          title: {
            text: response.impactName,
            style: {
              color: this.props.isOpportunity ? "#bfbf2d" : "#008FFB",
            },
          },
          labels: {
            formatter: function (value) {
              return formatNumber(value, "##,#");
            },
            rotate: -45,
            rotateAlways: true,
            offsetX: 6,
            offsetY: 0,
            axisTicks: {
              show: true,
            },
            // tickAmount: 10,
            // range: 10,
            labels: {
              show: true,
            },
            style: {
              colors: this.props.isOpportunity ? "#bfbf2d" : "#008FFB",
              fontSize: "10px",
              fontFamily: "Helvetica, Arial, sans-serif",
              fontWeight: 400,
              cssClass: "apexcharts-xaxis-label",
            },
          },
          tooltip: {
            enabled: false,
            // offsetX: 6,
            // offsetY: 0,
          },
        },

        yaxis: [
          {
            min: 0,
            max: 100,
            seriesName: "Probability (%)",
            tickAmount: 10,
            forceNiceScale: true,
            // opposite: true,
            fill: {
              type: "gradient",
            },
            axisTicks: {
              show: true,
            },
            axisBorder: {
              show: true,
              color: this.props.isOpportunity?"#bfbf2d":"#008FFB",
            },
            labels: {
              style: {
                colors: this.props.isOpportunity?"#bfbf2d":"#008FFB",
              },
              //   offsetX: -5,
              // offsetY: -5,
              formatter: function (value, index) {
                // var ss=response.sCurveValue[index];
                return value + "%";
              },
            },
            title: {
              text: "Probability (%)",
              style: {
                color: this.props.isOpportunity?"#bfbf2d":"#008FFB",
              },
            },
          },
        ],
        tooltip: {
          enabled: false,
          fixed: {
            enabled: false,
            position: "topLeft", // topRight, topLeft, bottomRight, bottomLeft
            offsetY: 30,
            offsetX: 60,
          },
          // theme: 'dark',

          x: {
            show: false,
          },
          enabledOnSeries: [],
        },
        legend: {
          horizontalAlign: "left",
          offsetX: 40,
		  labels:{
			  colors: "#f1f1f1"
		  }
        },
        colors: [this.props.isOpportunity ? "#bfbf2d" : "#008FFB", "#bfbf2d"],
        // colors: ["#bfbf2d","#008FFB","#00E396"]
      },
    };
  };

  handleEditCell = () => {};
  render() {
    const histogramResponse = this.props.histogramResponse
      ? this.props.histogramResponse
      : {
          impact: [],
          frequency: [],
          sCurveValue: [],
          sCurevePercentage: [],
          sCurvePercent: [],
          sCurvePercentile: [],
        };
    this.createHistogramChart(histogramResponse);
    return (
      <div id="chart">
        <div className="switchHist">
          <Switch
            onChange={(event) => {
              this.setState({ checked: event.target.value });
            }}
            checked={this.state.checked}
            onLabel={"Probability"}
            offLabel={"P-Value"}
            defaultChecked={false}
          />
        </div>
        <ul className="WidgetActions">
          <li title="PNG Download" onClick={this.onImageExportClick}>
            <a>
              <img src={Images.DOWNLOAD} alt="Download" width="14" />
            </a>
          </li>
          <li title="PDF Download" onClick={this.onPDFExportClick}>
            <a>
              <img src={Images.PDF} alt="Download" width="14" />
            </a>
          </li>
          <li>
            <EmailChartPopup
              onSendEmail={this.onEmailExportClick}
              widgetId={0}
              widgetName={this.props.title || (this.state.checked ? "Probability Histogram Chart" : "P-Value Histogram Chart")}
            />
          </li>
        </ul>
        {this.state.checked ? (
          //bar chart
          <ReactApexChart
            ref={this.chartRef}
            options={this.state.probabilityOptions}
            series={this.state.probabilitySeries}
            type="line"
            height={350}
          />
        ) : (
          //line chart
          <HistogramLineChart
            ref={this.lineChartRef}
            {...this.props}
            histogramResponse={histogramResponse}
            isOpportunity={this.props.isOpportunity}
            title={this.props.title}
          />
        )}

        <div className="colGridTable">
          {histogramResponse.sCurveValue.map((item, index) => (
            <div className="colGridBox">
              <h5>P({histogramResponse.sCurevePercentage[index]})</h5>
              <p title={formatNumber(item, "##,#")}>
                {formatNumber(item, "##,#")}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }
}
